import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import useWebSocket from "@/hooks/useWebSocket";
import { subscribeToPush, isPushSupported, getNotificationPermission, enablePushForUser } from "@/utils/pushNotifications";
import { pushEnvironment, isIOS } from "@/utils/platform";
import { playMessageSound, playPaymentSound, playFriendRequestSound, warmUpAudio } from "@/utils/notificationSounds";

// Lazy-loaded pages — only download code when navigating there
const Welcome = lazy(() => import("@/pages/Welcome"));
const Signup = lazy(() => import("@/pages/Signup"));
const Login = lazy(() => import("@/pages/Login"));
const ChatList = lazy(() => import("@/pages/ChatList"));
const Conversation = lazy(() => import("@/pages/Conversation"));
const AddFriend = lazy(() => import("@/pages/AddFriend"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Profile = lazy(() => import("@/pages/Profile"));
const CreateGroup = lazy(() => import("@/pages/CreateGroup"));
const PublicPay = lazy(() => import("@/pages/PublicPay"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Insights = lazy(() => import("@/pages/Insights"));
const ClaimPage = lazy(() => import("@/pages/ClaimPage"));
const FeedPage = lazy(() => import("@/pages/FeedPage"));
const InvitePage = lazy(() => import("@/pages/InvitePage"));
const WorldClock = lazy(() => import("@/pages/WorldClock"));
const Manifesto = lazy(() => import("@/pages/Manifesto"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Support = lazy(() => import("@/pages/Support"));
import { InstallPrompt } from "@/components/InstallPrompt";
import PushEnableBanner from "@/components/PushEnableBanner";
import { E2EProvider } from "@/contexts/E2EContext";
import { initDarkMode } from "@/utils/darkMode";

// Initialize dark mode from localStorage before render
initDarkMode();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Supported currencies
export const CURRENCIES = {
  GBP: { symbol: "£", name: "British Pound" },
  USD: { symbol: "$", name: "US Dollar" },
  EUR: { symbol: "€", name: "Euro" }
};

// Auth Context
const AuthContext = createContext(null);

// WebSocket Context
const WebSocketContext = createContext(null);

export const useAuth = () => useContext(AuthContext);
export const useWS = () => useContext(WebSocketContext);
export { useE2E } from "@/contexts/E2EContext";

export const AuthProvider = ({ children }) => {
  // Load cached user for instant display
  const cachedUser = (() => {
    try { return JSON.parse(localStorage.getItem("zap_user")); } catch { return null; }
  })();
  const [user, setUser] = useState(cachedUser);
  const [token, setToken] = useState(localStorage.getItem("zap_token"));
  const [loading, setLoading] = useState(!cachedUser && !!localStorage.getItem("zap_token"));
  
  // Global 401 interceptor — force logout on expired sessions
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          localStorage.removeItem("zap_token");
          localStorage.removeItem("zap_user");
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);
  
  // WebSocket hook
  const ws = useWebSocket(token);

  // Prefetch conversations data for instant ChatList
  const prefetchConversations = async (authToken) => {
    try {
      const res = await axios.get(`${API}/conversations/`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      sessionStorage.setItem("moji_convos", JSON.stringify(res.data));
    } catch {}
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          localStorage.setItem("zap_user", JSON.stringify(response.data));
          // Prefetch conversations + push notifications in parallel
          Promise.all([
            prefetchConversations(token),
            initPushNotifications(token)
          ]);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("zap_token");
          localStorage.removeItem("zap_user");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // Fix 1: Register service worker unconditionally on app init (before any push/install logic)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch((err) => {
        console.error('Service worker registration failed:', err);
      });
    }
  }, []);

  // PWA auto-update: check for new service worker on every focus/visibility change
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const checkForUpdate = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      } catch (e) { /* ignore */ }
    };

    // Check on visibility change (user switches back to app)
    const onVisibility = () => { if (document.visibilityState === 'visible') checkForUpdate(); };
    document.addEventListener('visibilitychange', onVisibility);

    // Check on focus (user taps app icon)
    window.addEventListener('focus', checkForUpdate);

    // Listen for new SW and auto-activate
    let refreshing = false;
    const onControllerChange = () => { if (!refreshing) { refreshing = true; window.location.reload(); } };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    // Initial check
    checkForUpdate();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  // Fix 3: Decouple permission request from subscription.
  // SW is already registered by Fix 1. Route by platform environment.
  const initPushNotifications = async (authToken) => {
    try {
      const env = pushEnvironment();
      if (env === 'native') {
        // Capacitor push init happens in its own path; nothing to do here
        return;
      }
      if (env === 'web-push' && !isIOS()) {
        // Android / desktop: existing 3-sec delay auto-prompt
        setTimeout(async () => {
          await enablePushForUser(authToken);
        }, 3000);
      } else if (env === 'web-push' && isIOS()) {
        // iOS PWA (standalone, 16.4+): must be user-gesture triggered
        setPushBannerState('ios-pwa-tap-to-enable');
      } else if (env === 'ios-needs-install') {
        // iOS Safari (not installed): show "Add to Home Screen" banner
        setPushBannerState('ios-safari-install-first');
      }
      // 'unsupported' — do nothing
    } catch (err) {
      console.error('Push init error:', err);
    }
  };

  // Global notification sound listeners
  useEffect(() => {
    if (!ws || !user) return;

    const removeMsg = ws.addListener('new_message', (data) => {
      // Don't play sound for own messages
      if (data.message?.sender_id !== user.id) {
        playMessageSound();
      }
    });

    const removePay = ws.addListener('payment_received', () => {
      playPaymentSound();
    });

    const removeFriend = ws.addListener('friend_request', () => {
      playFriendRequestSound();
    });

    return () => {
      removeMsg();
      removePay();
      removeFriend();
    };
  }, [ws, user]);

  // Badge management — clear on focus, set on new messages when backgrounded
  useEffect(() => {
    const clearBadge = () => {
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage('CLEAR_BADGE');
      }
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    };

    // Clear badge whenever app comes to foreground
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        clearBadge();
      }
    };

    // Clear on initial load
    clearBadge();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', clearBadge);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', clearBadge);
    };
  }, []);

  const [loginPin, setLoginPin] = useState(null);
  const [pushBannerState, setPushBannerState] = useState(null);

  const login = (newToken, userData, pin) => {
    localStorage.setItem("zap_token", newToken);
    localStorage.setItem("zap_user", JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    if (pin) setLoginPin(pin);
    warmUpAudio(); // Unlock Web Audio API on user interaction
    // Prefetch conversations immediately so ChatList loads instantly
    prefetchConversations(newToken);
  };

  const logout = () => {
    localStorage.removeItem("zap_token");
    localStorage.removeItem("zap_user");
    localStorage.removeItem("moji_install_dismiss_count");
    localStorage.removeItem("moji_install_dismissed");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("zap_user", JSON.stringify(userData));
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        localStorage.setItem("zap_user", JSON.stringify(response.data));
      } catch {}
    }
  };

  const getCurrencySymbol = () => {
    const currency = user?.currency || "GBP";
    return CURRENCIES[currency]?.symbol || "£";
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, refreshUser, getCurrencySymbol, loginPin, pushBannerState, setPushBannerState }}>
      <WebSocketContext.Provider value={ws}>
        <E2EProvider token={token} loginPin={loginPin}>
        {children}
        </E2EProvider>
      </WebSocketContext.Provider>
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="app-wrapper">
        <div className="mobile-frame items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/welcome" replace />;
  }
  
  return children;
};

// Public Route (redirect to app if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="app-wrapper">
        <div className="mobile-frame items-center justify-center">
          <div className="spinner" />
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/chats" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="app-wrapper">
          <div className="mobile-frame">
            <Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="spinner" /></div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/welcome" element={<PublicRoute><Welcome /></PublicRoute>} />
              <Route path="/manifesto" element={<Manifesto />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/support" element={<Support />} />
              <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              
              {/* Public Payment Link (no auth needed) */}
              <Route path="/pay/:emojiCode" element={<PublicPay />} />
              <Route path="/pay/success" element={<PublicPay />} />
              <Route path="/invite/:emojiCode" element={<InvitePage />} />
              
              {/* Claim Link (no auth needed to view) */}
              <Route path="/claim/:code" element={<ClaimPage />} />
              
              {/* Protected Routes */}
              <Route path="/chats" element={<ProtectedRoute><ChatList /></ProtectedRoute>} />
              <Route path="/chat/:conversationId" element={<ProtectedRoute><Conversation /></ProtectedRoute>} />
              <Route path="/add-friend" element={<ProtectedRoute><AddFriend /></ProtectedRoute>} />
              <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
              <Route path="/time" element={<ProtectedRoute><WorldClock /></ProtectedRoute>} />
              <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/groups/new" element={<ProtectedRoute><CreateGroup /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
              
              {/* Default */}
              <Route path="/" element={<Navigate to="/welcome" replace />} />
              <Route path="*" element={<Navigate to="/welcome" replace />} />
            </Routes>
            </Suspense>
          </div>
        </div>
        <Toaster position="top-center" richColors />
        <InstallPrompt />
        <PushEnableBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
